import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { resolveApiBaseUrl } from "@/lib/api/baseUrl";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formatDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

// Convert minutes to a formatted string
export const formatMinutes = (
  minutes?: number,
  defaultValue?: string
): string => {
  if (minutes === undefined) return `${defaultValue}m`;

  // Calculate hours and remaining minutes
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  // Build the formatted string
  if (hours > 0 && remainingMinutes > 0) {
    return `${hours}h ${remainingMinutes}m`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else {
    return `${remainingMinutes}m`;
  }
};

export const fileToDataURI = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result as string);
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsDataURL(file);
  });
};

export function formatBytes(
  bytes: number,
  opts: {
    decimals?: number;
    sizeType?: "accurate" | "normal";
  } = {}
) {
  const { decimals = 0, sizeType = "normal" } = opts;

  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const accurateSizes = ["Bytes", "KiB", "MiB", "GiB", "TiB"];
  if (bytes === 0) return "0 Byte";
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(decimals)} ${
    sizeType === "accurate" ? accurateSizes[i] ?? "Bytes" : sizes[i] ?? "Bytes"
  }`;
}

export function absoluteUrl(path: string) {
  return `${process.env.NEXT_PUBLIC_APP_URL}${path}`;
}

/**
 * Get the base URL for API calls, ensuring protocol consistency
 * Uses window.location.origin on client side to match current protocol (HTTP/HTTPS)
 */
/**
 * Delegates to the one implementation in `lib/api/baseUrl`.
 *
 * This used to return `window.location.origin` in the browser, ignoring
 * `NEXT_PUBLIC_API_ORIGIN` — so `webApiFetcher`, which imports THIS one and
 * carries most of the app's calls, could never reach `apps/api` directly
 * however the environment was set. Two functions with one name and different
 * answers; the difference was invisible until a rewrite bug 404'd every bare
 * collection path.
 */
export function getApiBaseUrl(path?: string): string {
  return resolveApiBaseUrl(path);
}

/**
 * Stole this from the @radix-ui/primitive
 * @see https://github.com/radix-ui/primitives/blob/main/packages/core/primitive/src/primitive.tsx
 */
export function composeEventHandlers<E>(
  originalEventHandler?: (event: E) => void,
  ourEventHandler?: (event: E) => void,
  { checkForDefaultPrevented = true } = {}
) {
  return function handleEvent(event: E) {
    originalEventHandler?.(event);

    if (
      checkForDefaultPrevented === false ||
      !(event as unknown as Event).defaultPrevented
    ) {
      return ourEventHandler?.(event);
    }
  };
}

// Helper function to remove undefined values from an object
export function removeUndefined<T extends object>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter((entry) => entry[1] !== undefined)
  ) as Partial<T>;
}
